/**
 * Config contract for `<app-nested-table>` — a multi-level table where each
 * expanded row opens the NEXT level as its own titled table (Customer →
 * Segment → Spec), every level with its own columns, cell types and actions.
 *
 * Everything is data: levels, columns, formatting, colours, editors, buttons.
 * Level N's rows are the `childrenField` array of a level N-1 row.
 */

import type { DisplayCurrency, TableCellConfig } from '../../../chart-components/unified-table-matrix-chart/table-cell-config';

export type NestedTableRow = Record<string, unknown>;

/** A filter accepted by the component compute TABLE API. `value` may be a
 * literal or a row token such as `[customer]` / `[segment]`. */
export interface NestedTableQueryFilter {
  dataset?: string;
  field: string;
  operator: string;
  value: unknown;
}

/** A selected dataset column or a calculated formula column. */
export interface NestedTableQueryColumn {
  dataset?: string;
  field?: string;
  displayName: string;
  aggregation?: string;
  formula?: string;
  filters?: NestedTableQueryFilter[];
}

/** One independent request to `/components/compute`. Level 0 loads initially;
 * later levels are executed when their parent row is expanded. */
export interface NestedTableLevelQuery {
  componentType: 'TABLE';
  title?: string;
  columns: NestedTableQueryColumn[];
  filters?: NestedTableQueryFilter[];
  sorting?: Array<{ field: string; direction: 'ASC' | 'DESC' }>;
  pagination?: { enabled: boolean };
}

/** Number scale of a column: auto picks K / M / B by size. */
export type NestedNumberScale = 'auto' | 'K' | 'M' | 'B';

export type NestedCellType =
  /** Plain text (default). */
  | 'text'
  /** Number with thousands separators; `prefix` / `suffix` / `decimals` apply. */
  | 'number'
  /** Number with a `$` prefix unless `prefix` says otherwise. */
  | 'currency'
  /** Number with a `%` suffix unless `suffix` says otherwise. */
  | 'percent'
  /** Value drawn as a pill (e.g. "MFN"). */
  | 'badge'
  /** 1-based position of the row within its level. */
  | 'index'
  /** Editable text / number box. */
  | 'input'
  /** Editable select over `options`. */
  | 'dropdown';

export type NestedColorOp = 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq' | 'empty' | 'notEmpty' | 'contains';

/** First matching rule wins. `value` is compared numerically when both sides are numbers. */
export interface NestedColorRule {
  op: NestedColorOp;
  value?: unknown;
  color?: string;
  background?: string;
  bold?: boolean;
}

export interface NestedOption {
  label: string;
  value: string;
}

export interface NestedBadgeStyle {
  background?: string;
  color?: string;
}

export interface NestedTableColumn {
  field: string;
  label: string;
  /**
   * ONE column drawn from TWO database columns ("Proposed Delta wrt MFN":
   * `6% - 10%`). The cell shows `field` and `secondField` side by side with
   * `separator` between them, both formatted by this column's type / prefix /
   * suffix / decimals. Input / dropdown columns get one editor per key, and
   * each edit reports the key it changed. Colour rules, data bars, click-to-
   * filter and the expand arrow read the first key.
   */
  secondField?: string;
  /** Text between the two values of a paired column. Default "-". */
  separator?: string;
  type?: NestedCellType;
  align?: 'left' | 'center' | 'right';
  /** px number or any CSS width ("12%"). */
  width?: number | string;
  /** Money column: shown × the dashboard's exchange rate (display only). Explicit false turns it off. */
  convertCurrency?: boolean;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  /** Show numbers in thousands / millions / billions (23,037,142 -> 23.0M); auto = by each value's size. */
  scale?: NestedNumberScale;
  /** Monospace digits / codes (e.g. "Q38-SS / TIER 4 FINAL"). */
  mono?: boolean;
  bold?: boolean;
  /** Static text colour for the whole column. */
  color?: string;
  colorRules?: NestedColorRule[];
  /** badge: default pill style, overridable per value through `badgeStyles`. */
  badge?: NestedBadgeStyle;
  badgeStyles?: Record<string, NestedBadgeStyle>;
  /** input / dropdown */
  placeholder?: string;
  options?: Array<string | NestedOption>;
  /** Adds " *" to the header and flags an empty editor. */
  required?: boolean;
  /** input / dropdown: the box shows its value but cannot be changed. */
  readOnly?: boolean;
  /** input: 'number' parses edits to numbers (default when prefix/suffix set). */
  inputType?: 'text' | 'number';
  /** Explicit per-column numeric-only editor toggle from the table builder. */
  numericOnly?: boolean;
  /** Typed number kept within these: a number, or a row column holding it (e.g. floor_price). */
  minValue?: string;
  maxValue?: string;
  /** % added to the min / max (signed: -10 lowers the min by 10%, 10 raises the max by 10%). Unset = 0. */
  minOffsetPct?: number;
  maxOffsetPct?: number;
  /** Several min / max columns: use the lowest or the highest of them.
   *  Unset = lowest for the minimum, highest for the maximum. */
  minPick?: 'lowest' | 'highest';
  maxPick?: 'lowest' | 'highest';
  /** The limits and the column each came from, under the field (true = shown; unset = hover only). */
  showBounds?: boolean;
  /** Message box under a value outside the range - only when set. Text: {min} {max} {value} {label}. */
  rangeMessage?: string;
  /** Shown for null / undefined / '' (falls back to the table's `emptyText`). */
  emptyText?: string;
  hidden?: boolean;
  /** This column carries its level's expand arrow (none ticked = the first column). */
  expander?: boolean;
  /** GENERIC cell configuration (table-cell-config.ts) — the ONE definition
   *  shared with the Normal / Editable / Drill-Down tables: display kind,
   *  colours, conditional rules (incl. cross-column), highlight, tooltip,
   *  editor + dropdown values. Folded together with the legacy keys above by
   *  nestedColumnCellConfig(); the explicit config wins. */
  cellConfig?: TableCellConfig;
}

export interface NestedTableAction {
  /** `export` is built in (CSV of that table); every action is also emitted. */
  id: string;
  label: string;
  icon?: string;
  /** outline / filled in the table's own colours, or link (text only). */
  style?: 'outline' | 'filled' | 'link';
  /** A colour preset instead of the table's colours (the shared button look editor). */
  tone?: string;
  /** "More style" keys of the shared button look editor (ButtonConfig style keys). */
  look?: Record<string, unknown>;
  /** What the button does in a dashboard (Design Builder runs it like a Button widget).
   *  `excel` = the platform's Excel export of the table's dataset, filtered by the dashboard's selections. */
  action?: 'export' | 'excel' | 'navigate' | 'api';
  /** excel: the sheet's name (default Sheet1). */
  sheetName?: string;
  /** excel: dataset columns sent as filters - the parent row's value, else the dashboard selection.
   *  Empty = every dashboard selection on the dataset's columns. (`apiUrl` / `apiBody` = the export call.) */
  filterColumns?: string[];
  /** navigate: url = a web address, dashboard = a published dashboard id, route = an app path. */
  navigateType?: 'url' | 'dashboard' | 'route';
  navigateTarget?: string;
  navigateNewTab?: boolean;
  /** api: the call made, with `{rows}` / `{parentRow}` / `{tableEdits}` tokens in the body. */
  apiMethod?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  apiUrl?: string;
  apiBody?: string;
}

export interface NestedTableLevel {
  /** Heading above this level's table (unused on level 0 — use `title`). */
  title?: string;
  /** Row key holding the next level's rows. Default `children`. */
  childrenField?: string;
  /** Show each row's 1-based number beside its configured column. */
  serialNumbers?: boolean;
  /** Explicit columns. Omit to derive them from this level's row keys. */
  columns?: NestedTableColumn[];
  /** Per-field tweaks applied on top of explicit or derived columns. */
  columnOverrides?: Record<string, Partial<NestedTableColumn>>;
  /** Display order by field; unlisted fields keep their place after these. */
  columnOrder?: string[];
  actions?: NestedTableAction[];
  /** A Save button on each edited row of this level, calling this API. */
  rowSave?: NestedTableRowSave;
  /** Start every row of this level expanded (a row's own `expanded` wins). */
  startExpanded?: boolean;
  /** Compact rows for dense levels. */
  dense?: boolean;
  /**
   * Row order of this level, applied in the table: the first key, then the next
   * for ties, and so on. A key is a column (its field or header) or a formula
   * over columns, e.g. `[Volume '26] + [Volume '27]` (the builder's "sum of").
   */
  sortBy?: NestedSortKey[];
  /** Opt-in: clicking a header sorts this level by it (again = the other way, a third time = back to `sortBy`). */
  headerSort?: boolean;
}

export interface NestedSortKey {
  /** A column's field / header, or a formula with `[Column]` names. */
  field: string;
  direction?: 'asc' | 'desc';
}

export interface NestedTableTheme {
  headerBackground: string;
  headerColor: string;
  rowBackground: string;
  expandedRowBackground: string;
  panelBackground: string;
  borderColor: string;
  textColor: string;
  accentColor: string;
  fontFamily: string;
  fontSize: number;
  radius: number;
}

export interface NestedTableData {
  /** The dashboard's picked currency: columns ticked "Convert currency" show value × rate. */
  currency?: DisplayCurrency | null;
  /** Uniform column width; 0 / absent keeps each column's automatic sizing. */
  tableHeaderWidth?: number;
  /** Per-level uniform column width; 0 / absent uses the all-levels width. */
  [key: `level${number}HeaderWidth`]: number | undefined;
  widgetId?: string;
  title?: string;
  subtitle?: string;
  levels: NestedTableLevel[];
  /** Level-0 rows inline; `rows` is accepted as an alias. Wins over `apiUrl`. */
  staticData?: NestedTableRow[];
  rows?: NestedTableRow[];
  /** Fetched through ConfigService when no rows are inline — a backend
   *  endpoint or a mock file such as `assets/chart-mock-data/nested-table-data.json`. */
  apiUrl?: string;
  /** HTTP method for `apiUrl` (ConfigService defaults to post; asset files need `get`). */
  method?: string;
  /** Key holding the rows inside the unwrapped response. Default `tableRows`. */
  rowsKey?: string;
  /** Compute endpoint and one TABLE request per rendered level. */
  levelApiUrl?: string;
  levelQueries?: NestedTableLevelQuery[];
  /** Canvas filters that target this table: sent with every child level it loads (see `canvasFilters` on the compute API). */
  canvasFilters?: NestedTableQueryFilter[];
  theme?: Partial<NestedTableTheme>;
  emptyText?: string;
  /**
   * User customisation layered over `levels` (what the Design Builder's
   * inspector writes): level settings plus per-field column overrides.
   */
  levelOverrides?: NestedTableLevelOverride[];
}

export interface NestedTableLevelOverride extends Partial<Omit<NestedTableLevel, 'columns' | 'columnOverrides' | 'columnOrder'>> {
  columns?: Record<string, Partial<NestedTableColumn>>;
  order?: string[];
}

/** Theme keys also accepted flat on the data object (inspector colour controls). */
export const NESTED_THEME_KEYS: ReadonlyArray<keyof NestedTableTheme> = [
  'headerBackground',
  'headerColor',
  'rowBackground',
  'expandedRowBackground',
  'panelBackground',
  'borderColor',
  'textColor',
  'accentColor',
  'fontFamily',
  'fontSize',
  'radius',
];

/**
 * Per-row Save — an UPDATE call. The body is fixed except for what is
 * configured here: the entity and the business keys (sent with their values),
 * plus the row's input / dropdown column values.
 */
export interface NestedTableRowSave {
  label?: string;
  url?: string;
  /** Entity the update targets (default: the table's dataset). */
  entity?: string;
  /** Columns identifying the row, e.g. ['cust_type', 'cust_spec_no']. */
  businessKeys?: string[];
  /** Columns allowed in the request; of these, only the ones the user edited
   *  in that row are sent. Empty = every edited column. */
  payloadColumns?: string[];
  /** When the server refuses the save and its answer contains this text (blank = any refusal),
   *  `refusedMessage` is shown in a popup. */
  refusedMatch?: string;
  refusedMessage?: string;
}

/** A row's Save was clicked. `done(ok)` tells the table how it went. */
export interface NestedTableRowSaveEvent {
  levelIndex: number;
  path: number[];
  row: NestedTableRow;
  save: NestedTableRowSave;
  /** The columns the user edited in this row since its last save. */
  editedFields: string[];
  /** The rows it sits under, top level first — a business key missing on the
   *  row itself (e.g. cust_type on level 1) is read from these. */
  ancestors: NestedTableRow[];
  done: (ok: boolean) => void;
}

export interface NestedTableCellChange {
  levelIndex: number;
  /** Row indices from the top level down to the edited row. */
  path: number[];
  field: string;
  value: unknown;
  row: NestedTableRow;
}

/** A button of a 'buttons' cell was clicked (TableCellConfig.buttons). */
export interface NestedTableCellButtonEvent {
  button: import('../../../chart-components/unified-table-matrix-chart/table-cell-config').TableCellButton;
  levelIndex: number;
  field: string;
  /** The clicked row (child rows left out). */
  row: NestedTableRow;
  /** The rows above it, top level first. */
  ancestors: NestedTableRow[];
}

export interface NestedTableActionEvent {
  actionId: string;
  levelIndex: number;
  /** Row whose expansion holds the table the action sits on (null on level 0). */
  parentRow: NestedTableRow | null;
  /** Every row above that table, top level first, ending with `parentRow` (empty on level 0). */
  ancestors?: NestedTableRow[];
  rows: NestedTableRow[];
}
